import { useEffect, useState } from "react";
import AdminAPIPage from "../../api/adminAPIs";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

interface TranscriptViewProps {
  meetingId: string;
}
const ReanalysisView: React.FC<TranscriptViewProps> = ({ meetingId }) => {
  const { showSnackbar } = useDisplaySnackbar();
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const queryClient = useQueryClient();
  const enabled = !!meetingId;
  const {
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    isSuccess,
    isError,
    error,
    data: meetingsData,
  } = AdminAPIPage.useGetMeetingTranscriptReanalysis(
    {
      meeting_id: meetingId,
      collateral_type: "reanalysis",
    },
    enabled
  );
  useEffect(() => {
    setShowSpinner(isDataLoading || isDataFetching);
    if (isSuccess) {
      const meetingtranscript = meetingsData?.data?.data?.reanalysis;
      setTranscript(meetingtranscript || "No transcript available");
      setOpenDialog(true);
    }
    if (isError) {
      showSnackbar("error", error);
    }
  }, [isDataLoading, isDataFetching, isSuccess, isError, meetingsData]);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTranscript(null);
  };

  const { mutate: mutateMeetingReanalysis } =
    AdminAPIPage.usePostAcceptMeetingReanalysis(meetingId || "", {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        showSnackbar("success", "ReAnalysis accepted and will be moved to analysis table");
        queryClient.invalidateQueries({
            queryKey: ["get_meeting_summary"],
          });
      },
      onError: (error: any) => {
        showSnackbar("error", error);
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });

  const handleAcceptAnalysis = () => {
    mutateMeetingReanalysis({
      meeting_id: meetingId,
      regenerated_update: "True",
    });
  };

  return (
    <div>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>ReAnalysis Summary</span>
          <IconButton onClick={handleCloseDialog} size="small">
            <Button onClick={handleCloseDialog}>Close</Button>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showSpinner ? (
            <div>Loading...</div>
          ) : (
            <pre className="whitespace-pre-wrap break-words p-4 bg-gray-100 rounded">
              {JSON.stringify(transcript, null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <button
            onClick={handleAcceptAnalysis}
            className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
          >
            Accept Analysis
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReanalysisView;
